import { ImagePaths } from '../../lib/imageUrls.js';

export const sc200SecurityOperationsAnalyst = {
  id: 52,
  slug: 'sc-200-security-operations-analyst',
  ogImage: '/og/courses/sc-200-security-operations-analyst.jpg',
  courseCode: 'SC-200-001',
  featured: false,
  title: 'SC-200 Microsoft Security Operations Analyst',
  subtitle: 'Threat detection, investigation and response with Microsoft Defender XDR and Sentinel',

  valueProposition: 'Security operations teams using Microsoft Sentinel and Defender XDR need engineers who can detect, investigate, and contain threats across endpoints, identities, email, and cloud workloads. This programme prepares analysts for the SC-200 exam while building practical SOC skills mapped to the ACSC Australian Signals Directorate threat intelligence frameworks and Essential Eight Maturity Level 2 detection controls.',

  duration: '32 hours (4 days intensive or 8 weeks part-time)',
  students: 'New',
  level: 'Intermediate',
  organization: 'Microsoft',
  image: ImagePaths.COURSE_2,
  shortDescription: 'SC-200-001 — Microsoft Security Operations Analyst certification preparation covering Sentinel, Defender XDR, threat hunting, and incident response for Australian SOC teams.',

  price: {
    aud: 1499,
    currency: 'AUD',
    earlyBird: 1199
  },

  stripeConfig: {
    priceId: 'price_1U3WMoKu1nyc322UOV1G4hcm',
    productId: 'prod_V3deUtkxsIJRJI'
  },

  snapshot: {
    whoItsFor: [
      'SOC analysts and tier 2/3 security engineers operating Microsoft Sentinel or Defender XDR',
      'Incident response practitioners handling Microsoft 365 and Azure threats',
      'Security engineers building detection rules and SOAR playbooks',
      'IT security staff preparing for the SC-200 Microsoft Associate certification'
    ],
    accreditation: 'Preparation for Microsoft Certified: Security Operations Analyst Associate (Exam SC-200)'
  },

  logistics: {
    classSize: 'Maximum 12 participants with live lab environment per analyst',
    requirements: [
      'Working knowledge of Microsoft 365, Azure fundamentals, and basic networking concepts',
      'Microsoft 365 E5 Security trial or existing Defender XDR access for labs',
      'Familiarity with KQL (Kusto Query Language) is beneficial but not required',
      'Reliable internet connection for streaming lab console access'
    ]
  },

  overview: 'The SC-200 Microsoft Security Operations Analyst programme trains practitioners to mitigate threats using Microsoft Defender XDR, Microsoft Sentinel, and the broader Microsoft security stack. The four exam domains — mitigate threats using Defender XDR, Defender for Cloud, Microsoft Sentinel, and manage SIEM/SOAR — are covered in sequence with hands-on KQL threat hunting labs throughout. In the Australian context, detection rules and investigation playbooks are mapped to ACSC threat intelligence priorities (ransomware, BEC, credential theft) and to the Essential Eight Maturity Level 2 detection requirements for patch compliance and privileged access monitoring. ACSC advisories and ASD-published threat reports are used as investigation scenario sources throughout the programme.',

  outcomes: [
    'Detect and investigate endpoint threats using Microsoft Defender for Endpoint across Windows, macOS, Linux, and mobile',
    'Investigate email and collaboration threats using Microsoft Defender for Office 365 and Attack Simulator',
    'Manage identity-based attack investigations using Microsoft Defender for Identity and Entra ID Protection',
    'Write KQL detection rules and hunting queries in Microsoft Sentinel for Australian SOC threat scenarios',
    'Configure SOAR playbooks using Logic Apps to automate incident triage and response steps',
    'Investigate multi-stage attack chains using Microsoft Sentinel incidents and Defender XDR correlation',
    'Map detections to MITRE ATT&CK techniques prioritised by ASD Essential Eight and ACSC threat intelligence',
    'Pass the Microsoft SC-200 Security Operations Analyst Associate exam'
  ],

  audience: [
    'Tier 1 to tier 3 SOC analysts working incidents in Microsoft Sentinel or Defender XDR',
    'Incident responders investigating ransomware, business email compromise and credential theft',
    'Detection engineers writing KQL analytics rules and tuning alert fidelity',
    'MSSP analysts operating multi-tenant Sentinel workspaces for Australian clients',
    'Infrastructure and Microsoft 365 administrators moving into a dedicated security operations role',
    'Security engineers preparing for the SC-200 Microsoft Associate certification'
  ],

  prerequisites: [
    'Working knowledge of Microsoft 365 and Azure fundamentals (AZ-900 or SC-900 level)',
    'Basic networking literacy — TCP/IP, DNS, authentication flows and log sources',
    'Access to a Microsoft 365 E5 Security trial or existing Defender XDR tenant for labs',
    'An Azure subscription (free tier is sufficient) for the Microsoft Sentinel workspace labs',
    'No prior KQL experience required — Module 3 starts from fundamentals'
  ],

  modules: [
    {
      number: 1,
      title: 'Mitigate Threats Using Microsoft Defender XDR',
      duration: '8 hours',
      topics: [
        'Microsoft Defender XDR unified portal: incident queue, advanced hunting, and alert correlation',
        'Defender for Endpoint: endpoint detection and response (EDR), device investigation, and live response',
        'Defender for Office 365: anti-phishing, safe links, safe attachments, and BEC investigations',
        'Defender for Identity: lateral movement detection, DCSync alerts, and Active Directory kill chain',
        'Defender for Cloud Apps: session policies, OAuth app governance, and shadow IT discovery'
      ]
    },
    {
      number: 2,
      title: 'Mitigate Threats Using Microsoft Defender for Cloud',
      duration: '6 hours',
      topics: [
        'Defender for Cloud: security posture, regulatory compliance, and workload protection plans',
        'Investigate alerts for servers, containers, databases, and storage workloads',
        'Just-in-time VM access and adaptive application controls for Essential Eight hardening',
        'Connect Defender for Cloud alerts to Microsoft Sentinel for unified SIEM investigation',
        'ASD Essential Eight Maturity Level 2 patching and hardening status via Defender for Cloud'
      ]
    },
    {
      number: 3,
      title: 'Microsoft Sentinel — SIEM Architecture and Detection',
      duration: '10 hours',
      topics: [
        'Sentinel workspace design: data connectors, log retention, and cost management for Australian SOCs',
        'KQL fundamentals to advanced: summarise, join, externaldata, and hunt queries',
        'Analytics rules: scheduled, NRT, and fusion rules for multi-stage attack detection',
        'Threat intelligence: STIX/TAXII feeds, ACSC indicators, and Microsoft Defender Threat Intelligence',
        'Workbooks and reporting: Essential Eight coverage dashboards and executive security metrics'
      ]
    },
    {
      number: 4,
      title: 'Incident Response and SOAR Playbooks',
      duration: '8 hours',
      topics: [
        'Sentinel incident management: triage, assignment, investigation graph, and entity timelines',
        'SOAR playbooks with Logic Apps: automated enrichment, containment, and notification workflows',
        'Ransomware response playbook: isolation, forensic preservation, and ASD notification workflow',
        'Business email compromise investigation: Office 365 audit logs, message trace, and consent phishing',
        'SC-200 exam preparation: mock exam walkthrough and KQL lab capstone'
      ]
    }
  ],

  format: {
    type: 'Live online training with a dedicated lab tenant — 4 days intensive or 8 weeks part-time',
    includes: [
      '32 hours of instructor-led SOC instruction with a live lab environment per analyst',
      'Hands-on KQL threat hunting labs building from fundamentals to cross-workspace correlation',
      'Investigation scenarios sourced from published ACSC advisories and OAIC breach reports',
      'Reusable Sentinel analytics rules, hunting queries and Logic Apps playbook templates',
      'Ransomware and business email compromise response runbooks you keep',
      'Timed SC-200 mock exam with domain-by-domain answer explanations',
      'Recorded sessions and lab guides for 6 months',
      'Certificate of completion and 30 days of post-course email support'
    ]
  },

  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Four-day intensive cohorts run monthly; the 8-week part-time stream runs two evenings per week so SOC rosters stay covered'
  },

  certification: 'Prepares participants for Microsoft Certified: Security Operations Analyst Associate via Exam SC-200. Exam voucher purchased separately through Microsoft Learn or Pearson VUE.',

  faqs: [
    {
      question: 'Do I need KQL experience before the course?',
      answer: 'No prior KQL experience is required. Module 3 starts from KQL fundamentals and builds to advanced hunting queries. Participants with existing KQL knowledge will move faster through the early content and spend more time on complex correlation scenarios.'
    },
    {
      question: 'Which Microsoft licence is needed for the lab environment?',
      answer: 'A Microsoft 365 E5 trial tenant covers Defender XDR, Defender for Office 365 P2, and Defender for Identity. A Microsoft Sentinel workspace (with a Log Analytics workspace) requires a separate Azure subscription — a free-tier Azure account is sufficient for lab volumes.'
    },
    {
      question: 'How does the course address Australian threat scenarios?',
      answer: 'Lab scenarios draw from publicly available ACSC advisories, ASD Essential Eight threat contexts, and OAIC Notifiable Data Breaches reports to reflect threats relevant to Australian organisations — ransomware targeting healthcare and local government, BEC targeting professional services, and credential theft targeting government Microsoft 365 tenants.'
    },
    {
      question: 'Is this course suitable for MSSP analysts?',
      answer: 'Yes. Managed security service provider analysts managing multiple Sentinel workspaces for Australian clients will find the multi-workspace querying, Lighthouse integration, and playbook automation content directly applicable to managed SOC operations.'
    }
  ],

  australianContext: true,
  complianceFrameworks: ['ASD Essential Eight', 'ACSC ISM', 'ACSC Threat Intelligence', 'Privacy Act 1988']
};
